import { useState, type FormEvent } from 'react';
import { DateOnlyInput } from '../components/DateOnlyInput';
type Row = Record<string, any>;
export type Field = {
  name: string;
  label: string;
  type?: string;
  options?: any[][];
  value?: any;
  optional?: boolean;
};
export function Choices({ name, title, options, initial = [] }: { name: string; title: string; options: Row[]; initial?: string[] }) {
  const [selected, setSelected] = useState(initial),
    [search, setSearch] = useState(''),
    [page, setPage] = useState(1),
    filtered = options.filter((d) => d.title.toLowerCase().includes(search.toLowerCase()));
  return (
    <fieldset>
      <legend>{title}</legend>
      <label>
        Find {title.toLowerCase()}
        <input
          type="search"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
      </label>
      {selected.map((id) => (
        <input key={id} type="hidden" name={name} value={id} />
      ))}
      <p>{selected.length} selected</p>
      {filtered.slice((page - 1) * 10, page * 10).map((d) => (
        <label key={d.id}>
          <input type="checkbox" checked={selected.includes(d.id)} onChange={(e) => setSelected((old) => (e.target.checked ? [...old, d.id] : old.filter((id) => id !== d.id)))} />
          {d.title}
        </label>
      ))}
      <button type="button" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
        Previous {title.toLowerCase()}
      </button>
      <button type="button" disabled={page * 10 >= filtered.length} onClick={() => setPage((p) => p + 1)}>
        Next {title.toLowerCase()}
      </button>
    </fieldset>
  );
}
export function Evidence({ documents }: { documents: Row[] }) {
  return <Choices name="documentIds" title="Supporting documents" options={documents} />;
}
export function CommandForm({
  title,
  fields,
  busy,
  docs,
  onSave,
  recipients = []
}: {
  title: string;
  fields: Field[];
  busy: boolean;
  docs: Row[];
  onSave: (v: Row, key: string) => Promise<Row>;
  recipients?: Row[];
}) {
  const [key, setKey] = useState(() => crypto.randomUUID());
  const [recipientKey, setRecipientKey] = useState(recipients.length === 1 ? recipients[0]!.key : '');
  const selectedRecipient = recipients.find((r) => r.key === recipientKey);
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget),
      v: Row = Object.fromEntries(form);
    v.documentIds = form.getAll('documentIds');
    for (const f of fields) {
      if (f.type === 'checkbox') v[f.name] = form.has(f.name);
      if (f.optional && v[f.name] === '') v[f.name] = null;
    }
    try {
      await onSave(v, key);
      setKey(crypto.randomUUID());
    } catch {
      /* Retain all fields on failure. */
    }
  }
  return (
    <details>
      <summary>{title}</summary>
      <form aria-label={title} onSubmit={submit}>
        <fieldset disabled={busy}>
          {fields.map((f) => (
            <label key={f.name + (['address', 'method'].includes(f.name) ? ':' + recipientKey : '')}>
              {f.type === 'checkbox' ? (
                <>
                  <input type="checkbox" name={f.name} defaultChecked={f.value ?? false} />
                  {f.label}
                </>
              ) : (
                <>
                  {f.label}
                  {f.options ? (
                    <select
                      aria-label={f.label}
                      name={f.name}
                      required={!f.optional}
                      defaultValue={f.value ?? (f.name === 'method' ? selectedRecipient?.method : undefined) ?? (f.options.length === 1 ? f.options[0]![0] : '')}
                      onChange={(e) => {
                        if (f.name === 'recipientKey') setRecipientKey(e.target.value);
                      }}
                    >
                      <option value="">Choose</option>
                      {f.options.map(([v, t]) => (
                        <option key={v} value={v}>
                          {t}
                        </option>
                      ))}
                    </select>
                  ) : f.type === 'date' ? (
                    <DateOnlyInput name={f.name} required={!f.optional} defaultValue={f.value ?? ''} />
                  ) : f.type === 'textarea' ? (
                    <textarea aria-label={f.label} name={f.name} required={!f.optional} defaultValue={f.value ?? (f.name === 'address' ? selectedRecipient?.address : '') ?? ''} />
                  ) : (
                    <input name={f.name} required={!f.optional} defaultValue={f.value ?? ''} />
                  )}
                </>
              )}
            </label>
          ))}
          {docs.length > 0 && <Evidence documents={docs} />}
          <label>
            Review note
            <textarea name="reason" required minLength={3} />
          </label>
          <button>Save</button>
        </fieldset>
      </form>
    </details>
  );
}
